import React, { createContext, useContext, ReactNode } from 'react';
import { useLoadScript } from '@react-google-maps/api';

export interface GoogleMapsScriptContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const defaultValue: GoogleMapsScriptContextValue = {
  isLoaded: false,
  loadError: undefined,
};

const GoogleMapsScriptContext =
  createContext<GoogleMapsScriptContextValue>(defaultValue);

/**
 * Inner component that calls useLoadScript only when we have an API key.
 * Prevents loading the Google script with an empty key (which causes
 * "This page didn't load Google Maps correctly" from Google).
 */
function GoogleMapsScriptLoader({
  apiKey,
  children,
}: {
  apiKey: string;
  children: ReactNode;
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ['places'] as unknown as string[],
  });

  return (
    <GoogleMapsScriptContext.Provider
      value={{ isLoaded, loadError: loadError ?? undefined }}
    >
      {children}
    </GoogleMapsScriptContext.Provider>
  );
}

export interface GoogleMapsScriptProviderProps {
  apiKey: string;
  children: ReactNode;
}

/**
 * Provides Google Maps script status. Only loads the script when apiKey is non-empty.
 * When apiKey is missing, we do not load the script and expose isLoaded: false so the
 * map UI can show a "not configured" message instead of Google's generic error.
 */
export function GoogleMapsScriptProvider({
  apiKey,
  children,
}: GoogleMapsScriptProviderProps) {
  const key = (apiKey ?? '').trim();

  if (!key) {
    return (
      <GoogleMapsScriptContext.Provider
        value={{
          isLoaded: false,
          loadError: new Error(
            'Google Maps API key not configured. Set PUBLIC_GOOGLE_MAPS_API_KEY in .env to enable the map.'
          ),
        }}
      >
        {children}
      </GoogleMapsScriptContext.Provider>
    );
  }

  return (
    <GoogleMapsScriptLoader apiKey={key}>{children}</GoogleMapsScriptLoader>
  );
}

export function useGoogleMapsScript(): GoogleMapsScriptContextValue {
  return useContext(GoogleMapsScriptContext) ?? defaultValue;
}
